import { UseCollection } from "@/common/composition/use-collection";
import { ListResult } from "@/common/models";
import { SharedState } from "@/common/state";
import { eventParser } from "@/gql/event-parser";
import { formatInput, gql2 } from "@/gql/lib";
import { inclusiveToDate } from "@/utils/date";
import {
  CoordinateInputTier3,
  Direction,
  EventsFilter,
  ExternalEventType,
  JustificationStatus,
  MarkedStatus,
  SortingKeys,
} from "@/gql/types";
import { UseCluster } from "./cluster/cluster.substate";
import { getMarkFilterValue } from "./mark-filter.util";

type EventsStatusFilters = {
  mark?: MarkedStatus | null;
  justification?: JustificationStatus | null;
  externalEventTypes?: ExternalEventType[] | null;
  fromDate?: string | null;
  toDate?: string | null;
};

interface EventsCollectionOptions {
  clusterUID?: string;
  statusFilters?: () => EventsStatusFilters;
}

type EventsLoadInput = {
  cursor: string;
  externalEventTypes?: ExternalEventType[] | null;
  fromDate?: string | null;
  toDate?: string | null;
};

/**
 * The fields an event is read with, shared by the events list and the
 * singular `event(id:)` query so a row and its detail parse the same way.
 */
export const EVENT_FIELDS = `
          id
          subType
          justification{
            id
            status
          }
          labels{
            id
            name
            type
            labelType
            labelCategory
            concreteScore
          }
          date
          sourceApplication
          insights{
            id
            name
            description
            severity
          }
          items{
            id
            name
            subType
            category
            extendedProperties{
              topic
            }
            archive{
              archiveName
              siblings
            }
            internalItems{
              name
              labels
            }
            clusterUID
            path
            labels{
              infoLabel{
                id
                name
              }
              adhocLabels{
                id
                name
              }
              piis{
                id
                name
                labelCategory
              }
              ctxLabels{
                id
                name
              }
            }
          }
          severity
          severityLevel
          mark
          triage{
            decision
            desc
          }
          relatedEntities{
            fileUploadPath
            exposedBy{
              entity{
                id
                email
                name
                type
                isExternal
                department
              }
            }
            exposedTo{
              relationType
              entity{
                id
                isExternal
                type
                isPersonalAddress
                name
                email
                department
              }
            }
          }
`;

const ENABLE_BACKEND_EVENT_TYPE_FILTER = true;

export const get_events = gql2<
  {
    cursor: string;
    cords: CoordinateInputTier3[];
    mark?: MarkedStatus | null;
    justification?: JustificationStatus | null;
    externalEventTypes?: ExternalEventType[] | null;
    fromDate?: string | null;
    toDate?: string | null;
  },
  ListResult<any>
>(
  "exposure-events",
  (
    { cords, cursor, mark, justification, externalEventTypes, fromDate, toDate },
    { isInfoTypeChildOfOne, default_label, indexed_insights },
  ) => {
    const q: EventsFilter = {
      resolveJustification: true,
      coordinates: cords,
      pagination: { key: SortingKeys.Date, direction: Direction.Descend },
      cursor,
    };
    const markFilterValue = getMarkFilterValue(mark);
    if (markFilterValue !== undefined) {
      q.mark = markFilterValue;
    }
    if (justification) {
      q.justification = justification;
    }
    if (ENABLE_BACKEND_EVENT_TYPE_FILTER && externalEventTypes?.length) {
      q.externalEventTypes = externalEventTypes;
    }
    if (fromDate) {
      q.fromDate = fromDate;
    }
    if (toDate) {
      // the filter's end date is the last day the user wants to see, and the
      // query's is exclusive — see inclusiveToDate
      q.toDate = inclusiveToDate(toDate);
    }
    return {
      query: `{
        #EXPORT
        events(filter:${formatInput(q)}){
          cursor
${EVENT_FIELDS}        }     
        #EXPORT_END  
      }`,
      mapper: (res) => {
        const { events } = res;
        // Debug: check if mark values are returned from server

        let cursor = "";
        if (events.length > 0) {
          cursor = events[0].cursor;
        }
        const objects = events.map(eventParser);
        return {
          cursor,
          objects: objects.map((d: any) => {
            return {
              ...d,
              hide_menu: false,
            };
          }),
        };
      },
    };
  },
);

export const UseEventsCollection = (
  input: () => CoordinateInputTier3[],
  options: EventsCollectionOptions = {},
) => {
  const getStatusFilters = () => {
    const filters = options.statusFilters?.();
    return {
      mark: filters?.mark ?? undefined,
      justification: filters?.justification ?? undefined,
      fromDate: filters?.fromDate ?? undefined,
      toDate: filters?.toDate ?? undefined,
    };
  };

  return UseCollection<any, EventsLoadInput>(async ({
    cursor,
    externalEventTypes,
    fromDate,
    toDate,
  }) => {
    const statusFilters = getStatusFilters();
    const mergedFilters = {
      ...statusFilters,
      ...(externalEventTypes !== undefined ? { externalEventTypes } : {}),
      ...(fromDate !== undefined ? { fromDate } : {}),
      ...(toDate !== undefined ? { toDate } : {}),
    };
    if (options.clusterUID) {
      const { load_cluster_events } = await UseCluster(
        options.clusterUID,
        (time_frame) => {
          return input().map((d) => {
            return {
              ...d,
              z: {
                type: d.z.type,
                values: [time_frame],
              },
            };
          });
        },
        {
          eventsFilters: () => mergedFilters,
        },
      );
      return await load_cluster_events();
    } else {
      const res = await get_events({
        cursor,
        cords: input(),
        ...mergedFilters,
      });
      if (
        !res.success &&
        mergedFilters.externalEventTypes?.length &&
        (res.code === 400 || res.code === 422)
      ) {
        return {
          success: true,
          value: {
            objects: [],
            cursor: "",
          },
        };
      }
      return res;
    }
  });
};
